import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Modal, notification, Skeleton } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiPublicSubscriberCancel, apiPublicSubscriberDetail, apiSubscriptionPlanDetails } from '../../../api/subscription';
import { formatDate, formatPrice } from '../../../utils/format';
import { frequencyNoun, subscriberStatusMeta } from '../../../utils/subscriptions';
import PublicStatusPage from '../../common/PublicStatusPage/PublicStatusPage';
import './SubscriberCancel.scss';

const SubscriberCancel = () => {
  const { planId, subscriberId } = useParams();
  const [planInfo, setPlanInfo] = useState(null);
  const [subscriber, setSubscriber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [planData, subscriberData] = await Promise.all([
        apiSubscriptionPlanDetails(planId),
        apiPublicSubscriberDetail(subscriberId),
      ]);
      setPlanInfo(planData);
      setSubscriber(subscriberData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(err);
    }
  }, [planId, subscriberId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelClick = () => {
    setConfirmModalVisible(true);
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    setConfirmModalVisible(false);

    try {
      const result = await apiPublicSubscriberCancel(subscriberId);
      setCancelSuccess(true);
      setSubscriber(result.subscriber || { ...subscriber, status: 'canceled' });
      notification.success({
        message: 'Sucesso',
        description: 'Sua assinatura foi cancelada com sucesso.',
        placement: 'topRight',
      });
    } catch (err) {
      notification.error({
        message: 'Erro',
        description: err.message || 'Ocorreu um erro ao cancelar a assinatura.',
        placement: 'topRight',
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const planUrl = `/assinar/${planId}`;
  const subscriptionUrl = `/assinar/${planId}/${subscriberId}`;
  const isAlreadyCanceled = subscriber?.status === 'canceled';

  if (error) {
    return error?.status === 404 ? (
      <PublicStatusPage
        className="SubscriberCancel"
        title="Cancelar Assinatura"
        merchantName={planInfo?.account_name}
        variant="neutral"
        heading="Não encontramos esta assinatura"
        body="O link pode estar incompleto ou a assinatura pode ter sido removida. Confira o endereço no e-mail que você recebeu."
        action={{ label: 'Ver o plano', to: planUrl }}
      />
    ) : (
      <PublicStatusPage
        className="SubscriberCancel"
        title="Cancelar Assinatura"
        merchantName={planInfo?.account_name}
        variant="warning"
        heading="Não conseguimos carregar esta assinatura"
        body="Isso costuma ser temporário."
        action={{ label: 'Tentar novamente', onClick: fetchData }}
      />
    );
  }

  if (loading) {
    return (
      <PublicStatusPage
        className="SubscriberCancel"
        title="Cancelar Assinatura"
        merchantName={planInfo?.account_name}
      >
        <Skeleton active />
      </PublicStatusPage>
    );
  }

  if (cancelSuccess) {
    return (
      <PublicStatusPage
        className="SubscriberCancel"
        title="Cancelar Assinatura"
        merchantName={planInfo?.account_name}
        variant="success"
        heading="Assinatura cancelada"
        body="Sua assinatura foi cancelada e você não receberá mais cobranças futuras."
        action={{ label: 'Ver minha assinatura', to: subscriptionUrl }}
      >
        <div className="subscription-info">
          <div className="plan-name">{planInfo?.name}</div>
          <div className="status canceled">Cancelado</div>
        </div>
      </PublicStatusPage>
    );
  }

  return (
    <>
      <PublicStatusPage
        className="SubscriberCancel"
        title="Cancelar Assinatura"
        merchantName={planInfo?.account_name}
        variant={isAlreadyCanceled ? 'neutral' : undefined}
        heading={isAlreadyCanceled ? 'Esta assinatura já foi cancelada' : undefined}
        body={
          isAlreadyCanceled
            ? 'Não há mais cobranças programadas para esta assinatura. Se quiser voltar, você pode assinar novamente quando quiser.'
            : undefined
        }
        action={isAlreadyCanceled ? { label: 'Assinar novamente', to: planUrl } : null}
      >
        <div className="subscriber-info">
          <h3>Dados da Assinatura</h3>
          <div className="info-row">
            <span className="label">Assinante:</span>
            <span className="value">{subscriber?.name}</span>
          </div>
          <div className="info-row">
            <span className="label">E-mail:</span>
            <span className="value">{subscriber?.email}</span>
          </div>
          <div className="info-row">
            <span className="label">Status:</span>
            <span className={`value status-badge ${subscriberStatusMeta(subscriber?.status).className}`}>
              {subscriberStatusMeta(subscriber?.status).label}
            </span>
          </div>
          {subscriber?.saved_card && (
            <div className="info-row">
              <span className="label">Cartão salvo:</span>
              <span className="value saved-card-tag">
                {subscriber.saved_card.card_brand} **** {subscriber.saved_card.last4_digits}
              </span>
            </div>
          )}
        </div>

        <div className="plan-info">
          <h3>Plano</h3>
          <div className="plan-card">
            <div className="plan-name">{planInfo?.name || subscriber?.plan?.name}</div>
            <div className="plan-price">
              {formatPrice(planInfo?.amount || subscriber?.plan?.amount)}
              <small>/{frequencyNoun(planInfo?.frequency || subscriber?.plan?.frequency)}</small>
            </div>
            {subscriber?.next_billing_date && !isAlreadyCanceled && (
              <div className="next-billing">
                Próxima cobrança: {formatDate(subscriber.next_billing_date)}
              </div>
            )}
          </div>
        </div>

        {!isAlreadyCanceled && (
          <div className="cancel-section">
            <div className="warning-box">
              <ExclamationCircleOutlined className="icon-warning" />
              <div className="warning-text">
                <strong>Atenção:</strong> Ao cancelar sua assinatura, você não terá mais acesso
                aos benefícios do plano e não receberá mais cobranças futuras.
              </div>
            </div>
            <Button
              type="primary"
              danger
              size="large"
              onClick={handleCancelClick}
              loading={cancelLoading}
              className="cancel-button"
            >
              Cancelar Assinatura
            </Button>
          </div>
        )}
      </PublicStatusPage>

      <Modal
        title="Confirmar Cancelamento"
        open={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setConfirmModalVisible(false)}>
            Voltar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={cancelLoading}
            onClick={handleConfirmCancel}
          >
            Confirmar Cancelamento
          </Button>,
        ]}
      >
        <p>Tem certeza que deseja cancelar sua assinatura?</p>
        <p>Esta ação não pode ser desfeita e você perderá acesso aos benefícios do plano.</p>
      </Modal>
    </>
  );
};

export default SubscriberCancel;
